import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button,
  Chip, IconButton, CircularProgress, Paper,
  TextField, InputAdornment, Stack, useTheme, useMediaQuery,
  Tabs, Tab, Badge, Divider, Menu, MenuItem
} from '@mui/material';
import {
  History as HistoryIcon,
  Chat as ChatIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  AutoStories as StoriesIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  AccessTime as RecentIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import { useLanguage } from "../context/LanguageContext";
import { getLanguageDisplayName } from '../config/languagesConfig';

// This one screen powers both "Logbook" (everything) and "Conversations"
// (pre-filtered) so there's a single, consistent design for past sessions.
const SessionsListPage = ({ defaultFilter = 'all', title = 'Logbook' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedLanguage } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State variables
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState(defaultFilter); // 'all', 'story', 'conversation'
  const [currentTab, setCurrentTab] = useState(0);

  // Load user sessions
  useEffect(() => {
    fetchSessions();
  }, []);

  // Keep the filter in sync when navigating between /sessions and /conversations
  // (React Router may reuse this component instance across those routes).
  useEffect(() => {
    setFilterType(defaultFilter);
  }, [defaultFilter]);

  // Fetch sessions from the API
  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await apiClient.sessions.getUserSessions();
      const sessionData = response.data.sessions || [];

      // Sort sessions by updated_at descending (newest first)
      const sortedSessions = [...sessionData].sort((a, b) => {
        return new Date(b.updated_at) - new Date(a.updated_at);
      });

      setSessions(sortedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete session
  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await apiClient.content.deleteSession(sessionId);
      setSessions(prev => prev.filter(session => session.session_id !== sessionId));
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  // Filter sessions
  const getFilteredSessions = () => {
    return sessions
      .filter(session => {
        // Apply search filter
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          return session.content_title.toLowerCase().includes(searchLower);
        }
        return true;
      })
      .filter(session => {
        // Apply language filter (if selectedLanguage is set)
        if (selectedLanguage) {
          return session.target_language === selectedLanguage;
        }
        return true;
      })
      .filter(session => {
        // Apply content type filter
        if (filterType === 'story') return session.content_type === 'story';
        if (filterType === 'conversation') return session.content_type === 'conversation';
        return true;
      });
  };

  // Get recent sessions (last 7 days)
  const getRecentSessions = () => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return getFilteredSessions().filter(session => {
      return new Date(session.updated_at) >= sevenDaysAgo;
    });
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Session Card Component
  const SessionCard = ({ session }) => {
    return (
      <Card
        sx={{
          transition: 'all 0.2s',
          '&:hover': {
            transform: isMobile ? 'none' : 'translateY(-2px)',
            boxShadow: 2
          }
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack spacing={1}>
            {/* Title and Type */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Typography
                variant={isMobile ? "body1" : "subtitle1"}
                sx={{
                  fontWeight: 500,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  flex: 1
                }}
              >
                {session.content_title}
              </Typography>
              <Chip
                icon={session.content_type === 'story' ? <StoriesIcon /> : <ChatIcon />}
                label={session.content_type === 'story' ? 'Story' : 'Chat'}
                size="small"
                variant="outlined"
                sx={{ flexShrink: 0 }}
              />
            </Box>

            {/* Language and Level */}
            <Stack direction="row" spacing={0.5}>
              <Chip
                label={getLanguageDisplayName(session.target_language)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 22 }}
              />
              <Chip
                label={session.proficiency_level}
                size="small"
                variant="outlined"
                sx={{ height: 22 }}
              />
            </Stack>

            {/* Date */}
            <Typography variant="caption" color="text.secondary">
              {formatDate(session.updated_at)}
            </Typography>
          </Stack>
        </CardContent>

        {/* Three action buttons like the original */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          <Button
            onClick={() => navigate(`/sessions/${session.session_id}`)}
            sx={{
              py: 1,
              backgroundColor: 'action.hover',
              borderRadius: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              '&:hover': { backgroundColor: 'action.selected' }
            }}
            startIcon={<ViewIcon fontSize="small" />}
          >
            Review
          </Button>

          <Button
            onClick={() => navigate(`/session/${session.session_id}`)}
            sx={{
              py: 1,
              backgroundColor: 'action.hover',
              borderRadius: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              color: 'success.main',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              '&:hover': { backgroundColor: 'action.selected' }
            }}
            startIcon={<PlayIcon fontSize="small" />}
          >
            Continue
          </Button>

          <Button
            onClick={(e) => handleDeleteSession(e, session.session_id)}
            sx={{
              py: 1,
              backgroundColor: 'action.hover',
              borderRadius: 0,
              color: 'error.main',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              '&:hover': { backgroundColor: 'error.light', color: 'error.contrastText' }
            }}
            startIcon={<DeleteIcon fontSize="small" />}
          >
            Delete
          </Button>
        </Box>
      </Card>
    );
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <Stack spacing={2}>
      {[1, 2, 3].map(i => (
        <Card key={i}>
          <CardContent>
            <Box sx={{ width: '100%', height: 120 }} />
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  const filteredSessions = getFilteredSessions();
  const recentSessions = getRecentSessions();

  // Tab content
  const tabContent = [
    {
      label: 'All',
      badge: filteredSessions.length,
      content: filteredSessions
    },
    {
      label: 'Recent',
      icon: <RecentIcon />,
      badge: recentSessions.length,
      content: recentSessions
    }
  ];

  return (
    <Box sx={{ pb: isMobile ? 10 : 8, minHeight: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 2 : 3,
          mb: 3,
          background:
            'radial-gradient(700px 240px at 85% -20%, rgba(198,152,43,0.28), transparent 60%),' +
            'linear-gradient(160deg, #0A3161 0%, #072347 100%)',
          backgroundColor: '#0A3161',
          color: 'white',
          borderRadius: 3,
          border: '1px solid rgba(198,152,43,0.35)',
        }}
      >
        <Stack spacing={1}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={600} sx={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            {title}
          </Typography>
          <Typography variant={isMobile ? "body2" : "body1"} sx={{ opacity: 0.9 }}>
            {selectedLanguage && `${getLanguageDisplayName(selectedLanguage)} • `}
            {filteredSessions.length} {filteredSessions.length === 1 ? 'session' : 'sessions'}
          </Typography>
        </Stack>
      </Paper>

      {/* Search and Filter Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: isMobile ? 2 : 2.5 }}>
          <Stack spacing={2}>
            {/* Search */}
            <TextField
              fullWidth
              size={isMobile ? "small" : "medium"}
              placeholder="Search your stories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Type Filter */}
            <Stack direction="row" spacing={1}>
              <Chip
                label="All"
                onClick={() => setFilterType('all')}
                color={filterType === 'all' ? 'primary' : 'default'}
                variant={filterType === 'all' ? 'filled' : 'outlined'}
              />
              <Chip
                icon={<StoriesIcon />}
                label="Stories"
                onClick={() => setFilterType('story')}
                color={filterType === 'story' ? 'primary' : 'default'}
                variant={filterType === 'story' ? 'filled' : 'outlined'}
              />
              <Chip
                icon={<ChatIcon />}
                label="Chats"
                onClick={() => setFilterType('conversation')}
                color={filterType === 'conversation' ? 'primary' : 'default'}
                variant={filterType === 'conversation' ? 'filled' : 'outlined'}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs
        value={currentTab}
        onChange={(e, newValue) => setCurrentTab(newValue)}
        sx={{ mb: 3 }}
      >
        {tabContent.map((tab, index) => (
          <Tab
            key={index}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {tab.icon}
                {tab.label}
                {tab.badge > 0 && (
                  <Chip label={tab.badge} size="small" sx={{ height: 20 }} />
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      {/* Content */}
      {loading ? (
        <LoadingSkeleton />
      ) : (
        <Box>
          {tabContent[currentTab].content.length > 0 ? (
            <Stack spacing={2}>
              {tabContent[currentTab].content.map(session => (
                <SessionCard key={session.session_id} session={session} />
              ))}
            </Stack>
          ) : (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: 'background.default',
                borderRadius: 2
              }}
            >
              <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No sessions found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {searchTerm || filterType !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Start your first learning session to see it here'}
              </Typography>
              {(searchTerm || filterType !== 'all') && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterType('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SessionsListPage;